import { useState, useCallback } from 'react';
import bridge from '@vkontakte/vk-bridge';

/**
 * Hook для работы с VK Bridge платежами
 * Инкапсулирует логику инициации платежей и обработки ошибок
 */
export const useVKPayments = () => {
    const [isProcessing, setIsProcessing] = useState(false);

    /**
     * Проверяет поддержку платежей в текущем окружении
     */
    const isPaymentSupported = useCallback(() => {
        return bridge.supports('VKWebAppShowOrderBox');
    }, []);

    /**
     * Инициирует покупку товара через VK Bridge
     * @param {string} itemId - ID товара для покупки
     * @returns {Promise<Object>} Результат операции
     */
    const initiatePurchase = useCallback(async (itemId) => {
        const operation = 'initiatePurchase';
        const startTime = Date.now();

        if (!isPaymentSupported()) {
            const error = {
                type: 'PAYMENT_NOT_SUPPORTED',
                message: 'Платежи не поддерживаются в данной версии приложения',
                originalError: null
            };
            logVKError(operation, error, { itemId });
            throw error;
        }

        // Валидация входных данных
        if (!itemId || typeof itemId !== 'string') {
            const error = {
                type: 'INVALID_ITEM_ID',
                message: 'Некорректный ID товара',
                originalError: null
            };
            logVKError(operation, error, { itemId });
            throw error;
        }

        setIsProcessing(true);
        
        try {
            console.log(`[${operation}] Starting VK payment for item:`, itemId);
            
            // Добавляем таймаут для VK Bridge запроса
            const paymentPromise = bridge.send('VKWebAppShowOrderBox', {
                type: 'item',
                item: itemId
            });

            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Payment timeout')), 60000); // 60 секунд
            });

            const result = await Promise.race([paymentPromise, timeoutPromise]);

            console.log(`[${operation}] VK payment result:`, {
                itemId,
                success: result.success,
                duration: Date.now() - startTime
            });
            
            return {
                success: result.success || false,
                data: result
            };
        } catch (error) {
            console.error(`[${operation}] VK payment error:`, error);
            
            // Нормализуем ошибки VK Bridge
            const normalizedError = normalizeVKError(error);
            logVKError(operation, normalizedError, { 
                itemId, 
                duration: Date.now() - startTime 
            });
            throw normalizedError;
        } finally {
            setIsProcessing(false);
        }
    }, [isPaymentSupported]);

    /**
     * Логирует ошибки VK Bridge с дополнительной информацией
     * @param {string} operation - Название операции
     * @param {Object} error - Ошибка
     * @param {Object} context - Дополнительный контекст
     */
    const logVKError = (operation, error, context = {}) => {
        const logData = {
            operation,
            error: {
                type: error.type || 'UNKNOWN',
                message: error.message,
                code: error.code,
                error_type: error.originalError?.error_type,
                error_data: error.originalError?.error_data,
            },
            context: {
                ...context,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                vkBridgeSupported: bridge.supports('VKWebAppShowOrderBox'),
            }
        };

        console.error(`VK Payments Error [${operation}]:`, logData);

        // В продакшене можно отправлять критичные ошибки в систему мониторинга
        if (process.env.NODE_ENV === 'production' && error.type !== 'USER_CANCELLED') {
            // Например: sendToErrorTracking(logData);
        }
    };

    /**
     * Нормализует ошибки VK Bridge для удобной обработки
     * @param {Object} error - Ошибка от VK Bridge
     * @returns {Object} Нормализованная ошибка
     */
    const normalizeVKError = (error) => {
        // Обработка случая, когда пользователь закрыл окно платежа
        if (error.error_type === 'client_error' && error.error_data?.error_reason === 'user_denied') {
            return {
                type: 'USER_CANCELLED',
                message: 'Покупка отменена пользователем',
                originalError: error
            };
        }

        if (error.error_data) {
            const { error_code, error_msg } = error.error_data;
            
            const errorMap = {
                1: { type: 'GENERAL_ERROR', message: 'Общая ошибка платежной системы' },
                2: { type: 'INVALID_PARAMS', message: 'Некорректные параметры платежа' },
                3: { type: 'INSUFFICIENT_FUNDS', message: 'Недостаточно голосов для покупки' },
                4: { type: 'USER_CANCELLED', message: 'Покупка отменена пользователем' },
                5: { type: 'PAYMENT_FAILED', message: 'Платеж не прошел' },
                10: { type: 'PAYMENTS_DISABLED', message: 'Платежи временно недоступны' },
                11: { type: 'ITEM_UNAVAILABLE', message: 'Товар недоступен для покупки' },
                12: { type: 'INVALID_ITEM', message: 'Неверный ID товара' },
                20: { type: 'RATE_LIMIT', message: 'Слишком много запросов. Попробуйте позже.' },
                100: { type: 'TECHNICAL_ERROR', message: 'Технические проблемы. Попробуйте позже.' }
            };

            const mappedError = errorMap[error_code];
            
            return {
                type: mappedError?.type || 'VK_ERROR',
                message: mappedError?.message || error_msg || 'Ошибка платежной системы',
                code: error_code,
                originalError: error
            };
        }

        // Обработка других типов отмены
        if (error.message && (error.message.includes('cancel') || error.message.includes('denied'))) {
            return {
                type: 'USER_CANCELLED',
                message: 'Покупка отменена пользователем',
                originalError: error
            };
        }

        // Обработка таймаутов
        if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
            return {
                type: 'TIMEOUT_ERROR',
                message: 'Превышено время ожидания. Попробуйте снова.',
                originalError: error
            };
        }

        // Обработка сетевых ошибок
        if (error.message && error.message.includes('network')) {
            return {
                type: 'NETWORK_ERROR',
                message: 'Проблемы с соединением. Проверьте интернет.',
                originalError: error
            };
        }

        return {
            type: 'UNKNOWN_ERROR',
            message: 'Неизвестная ошибка. Попробуйте позже.',
            originalError: error
        };
    };

    /**
     * Получает информацию о пользователе (если доступно)
     * @returns {Promise<Object|null>} Информация о пользователе или null
     */
    const getUserInfo = useCallback(async () => {
        try {
            if (bridge.supports('VKWebAppGetUserInfo')) {
                const userInfo = await bridge.send('VKWebAppGetUserInfo');
                return userInfo;
            }
            return null;
        } catch (error) {
            console.log('Could not get user info:', error);
            return null;
        }
    }, []);

    return {
        isProcessing,
        isPaymentSupported,
        initiatePurchase,
        getUserInfo
    };
};